import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { DataTable } from '../components/DataTable';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { usePaginatedChanges, usePaginatedExecutions } from '../hooks/useMonitoringStats';
import { useOrganizations } from '../hooks/useOrganizations';
import { formatDate, formatDateTime } from '../lib/format';
import type { Change, Execution } from '../types/domain';

type Tab = 'executions' | 'changes';

function Pagination({
  page,
  total,
  perPage,
  onPage,
}: {
  page: number;
  total: number;
  perPage: number;
  onPage: (p: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4">
      <span className="text-sm text-muted-foreground">
        Página {page} de {totalPages}
      </span>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onPage(Math.max(1, page - 1))}
          disabled={page === 1}
        >
          <ChevronLeft size={14} />
          Anterior
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onPage(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
        >
          Siguiente
          <ChevronRight size={14} />
        </Button>
      </div>
    </div>
  );
}

export function Monitoring() {
  const [tab, setTab] = useState<Tab>('executions');
  const [orgFilter, setOrgFilter] = useState<string>('');
  const [execPage, setExecPage] = useState(1);
  const [changesPage, setChangesPage] = useState(1);
  const perPage = 20;

  const { data: orgsData } = useOrganizations();
  const orgs = orgsData ?? [];

  const { data: execData, isLoading: execLoading } = usePaginatedExecutions(
    orgFilter || undefined,
    execPage,
    perPage
  );
  const { data: changesData, isLoading: changesLoading } = usePaginatedChanges(
    orgFilter || undefined,
    changesPage,
    perPage
  );

  const executionColumns = [
    { header: 'Organización', accessor: (e: Execution) => e.org_name },
    {
      header: 'Tipo',
      accessor: (e: Execution) => (e.search_type === 'name' ? 'Por nombre' : 'Por proceso'),
    },
    { header: 'Inicio', accessor: (e: Execution) => formatDateTime(e.started_at) },
    { header: 'Procesos', accessor: (e: Execution) => String(e.processes_found) },
  ];

  const changeColumns = [
    { header: 'Organización', accessor: (c: Change) => c.org_name ?? '—' },
    {
      header: 'Radicado',
      accessor: (c: Change) => <span className="font-mono text-xs">{c.numero_radicacion}</span>,
    },
    { header: 'Fecha', accessor: (c: Change) => formatDate(c.created_at) },
  ];

  const tabClasses = (active: boolean) =>
    `px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
      active
        ? 'border-primary text-foreground'
        : 'border-transparent text-muted-foreground hover:text-foreground'
    }`;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Monitoreo</h1>

      <div className="flex items-center gap-4">
        <div className="flex border-b border-border">
          <button
            type="button"
            className={tabClasses(tab === 'executions')}
            onClick={() => setTab('executions')}
          >
            Ejecuciones
          </button>
          <button
            type="button"
            className={tabClasses(tab === 'changes')}
            onClick={() => setTab('changes')}
          >
            Cambios
          </button>
        </div>

        <div className="ml-auto w-48">
          <Select
            value={orgFilter}
            onValueChange={(v) => {
              setOrgFilter(v === '_all' ? '' : v);
              setExecPage(1);
              setChangesPage(1);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Todas las orgs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Todas las orgs</SelectItem>
              {orgs.map((org) => (
                <SelectItem key={org.id} value={org.id}>
                  {org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {tab === 'executions' && (
        <>
          <DataTable
            columns={executionColumns}
            data={execData?.data ?? []}
            isLoading={execLoading}
            emptyMessage="Sin ejecuciones."
            getRowKey={(e) => e.snapshot_id}
          />
          <Pagination
            page={execPage}
            total={execData?.total ?? 0}
            perPage={perPage}
            onPage={setExecPage}
          />
        </>
      )}

      {tab === 'changes' && (
        <>
          <DataTable
            columns={changeColumns}
            data={changesData?.data ?? []}
            isLoading={changesLoading}
            emptyMessage="Sin cambios."
            getRowKey={(c) => c.id}
          />
          <Pagination
            page={changesPage}
            total={changesData?.total ?? 0}
            perPage={perPage}
            onPage={setChangesPage}
          />
        </>
      )}
    </div>
  );
}
